<script lang="ts">
import { computed, defineComponent, inject, onActivated, onDeactivated, PropType, ref, watch } from "vue";
import Steps from "@/components/Steps.vue";
import formValidierung from "@/businesslogic/formValidierung";
import Modal from "@/components/Modal.vue";
import Gewerbeinformationen from "@/components/gewerbe/Gewerbeinformationen.vue";
import GewerbeinformationenMakler from "@/components/gewerbe/GewerbeinformationenMakler.vue";
import Sanierungsfragen from "@/components/sanierung/Sanierungsfragen.vue";
import Infotext from "@/components/Infotext.vue";
import Adresse from "@/components/person/Adresse.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import { NavvItem } from "@/model/NavItem";
import IconSprite from "@/components/IconSprite.vue";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import Zeile from "@/components/grid/Zeile.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zelle from "@/components/grid/Zelle.vue";
import MaklerHinweis from "@/views/vgv/modals/MaklerHinweis.vue";
import Logger from "js-logger";
import GeschuetzterRouterLink from "@/components/GeschuetzterRouterLink.vue";
import BauartklasseErmittlung from "@/components/bauartklasse/BauartklasseErmittlung.vue";
import GebaeudeTyp from "@/components/GebaeudeTyp.vue";
import sanierungUtils from "@/businesslogic/utils/sanierungUtils";
import dateUtils from "@/businesslogic/utils/dateUtils";
import AdressenDatenService from "@/businesslogic/services/AdressenDatenService";
import dataUtils from "@/businesslogic/utils/dataUtils";
import { Gewerbe } from "@/model/Gewerbe";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "BasisdatenVGV",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  inheritAttrs: false,
  components: {
    GebaeudeTyp,
    BauartklasseErmittlung,
    Adresse,
    ValidierungsFehler,
    Sanierungsfragen,
    Gewerbeinformationen,
    GewerbeinformationenMakler,
    Modal,
    Steps,
    Infotext,
    IconSprite,
    Ueberschrift,
    Zeile,
    TitelZelle,
    Zelle,
    MaklerHinweis,
    GeschuetzterRouterLink,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
    anfrageId: String,
  },
  setup(_props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const feldBlurFocus: any = inject("feldBlurFocus");
    const { getIsoDatePlusYearsPlusMonths } = dateUtils();
    const { gt, lt } = dataUtils();

    const fehler = ref({} as any);
    const submitDeaktiviert = ref(false);

    const addresseRef = ref<InstanceType<typeof Adresse>>();

    const showables = ref({
      toggleFerienhaus: false,
      toggleLeerstand: false,
      toggleDenkmalschutz: false,
    });

    const { erzwingeGanzzahl } = formValidierung();
    const { pruefeSanierung, gebaeudeAelter30, gebaeudeAelter50 } = sanierungUtils();
    const { dateStringToIsoDate } = dateUtils();

    const naechstesJahr = new Date(getIsoDatePlusYearsPlusMonths(1, 3)).getFullYear();
    const baujahrFehlerText = "Bitte geben Sie das vierstellige Baujahr (1800-" + naechstesJahr + ") ein.";

    watch(
      () => piniaStore.state.gebaeudedaten.gewerbeVorhanden,
      (neu) => {
        if (!piniaStore.state.isMakler) {
          piniaStore.state.gebaeudedaten.gewerbeAnzahl = neu ? 1 : null;
        }
      },
    );

    const pruefeLeerstandUeber50 = (leerstand: number | null) => {
      if (piniaStore.state.isMakler && leerstand !== null && leerstand > 50) {
        submitDeaktiviert.value = true;
        toggleLeerstand();
      } else {
        submitDeaktiviert.value = false;
      }
    };

    const aelterAls50 = computed(() => {
      return gebaeudeAelter50(dateStringToIsoDate(piniaStore.state.tarifinformationen.versicherungsbeginn));
    });

    const aelterAls30 = computed(() => {
      return gebaeudeAelter30(dateStringToIsoDate(piniaStore.state.tarifinformationen.versicherungsbeginn));
    });

    const kernsanierungDurchgefuehrt = computed(() => {
      return piniaStore.state.gebaeudedaten.sanierungsdaten.kernsanierungDurchgefuehrt;
    });

    function maklerWechsel() {
      window.parent.postMessage(
        {
          func: "Streckenwechsel",
          message: "FH",
        },
        process.env.VUE_APP_MAKLERPORTAL_URL,
      );
    }

    const toggleDenkmalschutz = () => {
      showables.value.toggleDenkmalschutz = !showables.value.toggleDenkmalschutz;
    };

    const toggleLeerstand = () => {
      showables.value.toggleLeerstand = !showables.value.toggleLeerstand;
    };

    const toggleFerienhausCheck = () => {
      showables.value.toggleFerienhaus = !showables.value.toggleFerienhaus;
    };

    const checkPlzValidierung = () => {
      if (!piniaStore.state.plzValidiert) {
        fehler.value["risikoPostalCode"] = "Bitte geben Sie eine gültige Postleitzahl für den Ort ein";
      }
    };

    const mehrereGewerbeflaechenGewaehlt = () => {
      return (
        piniaStore.state.isMakler &&
        piniaStore.state.gebaeudedaten.gewerbeVorhanden === true &&
        gt(piniaStore.state.gebaeudedaten.gewerbeAnzahl, 1)
      );
    };

    const addiereGewerbeflaechen = (gesamteGewerbeflaeche: number, gewerbeCount: number) => {
      return gesamteGewerbeflaeche + getFlaecheVonGewerbe(piniaStore.state.gebaeudedaten.gewerbeListe[gewerbeCount]);
    };

    const checkGewerbeflaechen = () => {
      if (mehrereGewerbeflaechenGewaehlt()) {
        let gesamteGewerbeflaeche = 0;
        for (let gewerbeCount = 0; gt(piniaStore.state.gebaeudedaten.gewerbeAnzahl, gewerbeCount); gewerbeCount++) {
          if (piniaStore.state.gebaeudedaten.gewerbeListe[gewerbeCount].flaeche !== undefined) {
            gesamteGewerbeflaeche = addiereGewerbeflaechen(gesamteGewerbeflaeche, gewerbeCount);
          }
        }
        if (gesamteGewerbeflaeche > 49) {
          fehler.value["gewerbeflaeche" + piniaStore.state.gebaeudedaten.gewerbeAnzahl] =
            "Überwiegend gewerblich genutzte Gebäude können nicht versichert werden. Bitte prüfen Sie die einzelnen Angaben zur Gewerbefläche in %.";
        }
      }
    };

    const getFlaecheVonGewerbe = (gewerbe: Gewerbe): number => {
      return gewerbe.flaeche ?? 0;
    };

    const adresseGeaendert = ref(true);

    const eingabenSindValide = async () => {
      const { validiereForm, getFormFehlerAnzahl, getFormFehler } = formValidierung();

      const risikoAdresseValide = await addresseRef.value?.pruefen(
        true,
        (verlaesslich) => (piniaStore.state.risikoAnschrift.wurdeGeprueft = verlaesslich),
      );

      let denkmalschutzWeichtAb = false;

      const fehlerVorhanden = await validiereForm();

      if (risikoAdresseValide && !fehlerVorhanden) {
        const res = await AdressenDatenService.denkmalschutzErmitteln(piniaStore.state);
        if (res) {
          piniaStore.state.gebaeudedaten.denkmalschutz = res.data.gebaeudedaten.denkmalschutz;
        }

        if (
          piniaStore.state.isMakler &&
          adresseGeaendert.value &&
          piniaStore.state.gebaeudedaten.denkmalschutz &&
          !piniaStore.state.gebaeudedaten.denkmalschutzManuell
        ) {
          denkmalschutzWeichtAb = true;
          toggleDenkmalschutz();
        }

        Logger.debug(
          "Denkmalschutz",
          "adresseGeaendert: " + adresseGeaendert.value,
          "Denkmalschutz: " + piniaStore.state.gebaeudedaten.denkmalschutz,
          "Denkmalschutz manuell: " + piniaStore.state.gebaeudedaten.denkmalschutzManuell,
          "Denkmalschutz weicht ab: " + denkmalschutzWeichtAb,
        );

        adresseGeaendert.value = false;
      }

      Logger.debug("Anzahl Fehler aus Validierung: " + getFormFehlerAnzahl(), risikoAdresseValide, getFormFehler());
      fehler.value = getFormFehler();
      checkPlzValidierung();
      checkGewerbeflaechen();

      return risikoAdresseValide && getFormFehlerAnzahl() === 0 && !denkmalschutzWeichtAb;
    };

    const nextPage = () => {
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          if (
            piniaStore.state.gebaeudedaten.gebaeudesubtyp === "mfh" &&
            lt(piniaStore.state.gebaeudedaten.anzahlWohnungen, 2)
          ) {
            piniaStore.state.gebaeudedaten.anzahlWohnungen = 2;
          }
          emit("next-page", { pageIndex: 0 });
        }
      });
    };

    watch(
      () => piniaStore.state.gebaeudedaten.gebaeudenutzung,
      (neu) => {
        submitDeaktiviert.value = neu === "0002";
        if (neu === "0002") {
          toggleFerienhausCheck();
        } else if (piniaStore.state.isMakler && neu === "0004") {
          submitDeaktiviert.value = true;
          toggleLeerstand();
        } else {
          pruefeLeerstandUeber50(piniaStore.state.gebaeudedaten.leerstand);
        }
      },
    );

    watch(
      () => piniaStore.state.gebaeudedaten.baujahr,
      (wertNeu, wertAlt) => {
        if (wertAlt != wertNeu) {
          piniaStore.griLoeschen();

          if (!aelterAls50.value && piniaStore.state.gebaeudedaten.sanierungsdaten.kernsanierungDurchgefuehrt) {
            piniaStore.state.gebaeudedaten.sanierungsdaten.kernsanierungDurchgefuehrt = false;
          }
        }
      },
    );

    watch(
      () => [
        piniaStore.state.risikoAnschrift.plz,
        piniaStore.state.risikoAnschrift.ort,
        piniaStore.state.risikoAnschrift.strasse,
        piniaStore.state.risikoAnschrift.hausnummer,
      ],
      (werteNeu, werteAlt) => {
        if (werteAlt !== werteNeu) {
          adresseGeaendert.value = true;
          piniaStore.state.gebaeudedaten.denkmalschutz = false;
          piniaStore.zuersUndGriLoeschen();
        }
      },
    );

    watch(
      () => piniaStore.state.gebaeudedaten.leerstand,
      (neu) => {
        pruefeLeerstandUeber50(neu);
      },
    );

    onActivated(() => {
      pruefeSanierung(piniaStore.state.tarifinformationen.versicherungsbeginn);
    });

    onDeactivated(() => {
      pruefeSanierung(piniaStore.state.tarifinformationen.versicherungsbeginn);
    });

    return {
      piniaStore,
      nextPage,
      feldBlurFocus,
      fehler,
      showables,
      toggleFerienhausCheck,
      aelterAls30,
      aelterAls50,
      istSichtbar,
      wechsleSichtbarkeit,
      submitDeaktiviert,
      naechstesJahr,
      baujahrFehlerText,
      erzwingeGanzzahl,
      toggleLeerstand,
      addresseRef,
      maklerWechsel,
      toggleDenkmalschutz,
      kernsanierungDurchgefuehrt,
    };
  },
});
</script>

<template>
  <steps v-bind:items="navItems" :active="0" @goto-page="$emit('goto-page', $event)" />

  <section>
    <ueberschrift>Angaben zum Gebäude</ueberschrift>
    <gebaeude-typ :fehler="fehler" />
    <adresse
      :fehler="fehler"
      :placeholder="true"
      v-model:plz.lazy="piniaStore.state.risikoAnschrift.plz"
      v-model:ort.lazy="piniaStore.state.risikoAnschrift.ort"
      v-model:strasse.lazy="piniaStore.state.risikoAnschrift.strasse"
      v-model:hausnummer.lazy="piniaStore.state.risikoAnschrift.hausnummer"
      v-model:zu-pruefen="piniaStore.state.risikoAnschrift.zuPruefen"
      :feldNamen="{
        plz: 'risikoPostalCode',
        ort: 'risikoCity',
        strasse: 'risikoStreet',
        hausnummer: 'risikoStreetNumber',
      }"
      ref="addresseRef"
      session-marker="risikoAnschrift"
    >
      <template v-slot:plzOrtLabel>
        <p class="u-text-align-special">
          <strong>Anschrift</strong> des Gebäudes
          <span class="c-notice-icon" @click="wechsleSichtbarkeit('infoAnschrift')">
            <icon-sprite icon-name="icon-info-i" />
          </span>
        </p>
      </template>
      <template v-slot:strasseHausnummerLabel><span class="mobil-hidden">&nbsp;</span></template>
    </adresse>
    <zeile>
      <div
        class="o-grid__cell o-grid__cell--no-space o-grid__cell--12 o-grid__cell--6-1024 o-grid__cell--offset-4-1024"
      >
        <infotext
          name="infoAnschrift"
          css-klassen="o-grid__cell--no-space"
          text="Diese Angaben werden für die Beitragsberechnung benötigt."
        >
        </infotext>
      </div>
    </zeile>
    <zeile>
      <titel-zelle><strong>Baujahr</strong> des Gebäudes</titel-zelle>
      <zelle :breite="3">
        <input
          type="number"
          id="baujahr"
          name="baujahr"
          v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.baujahr"
          @keydown="erzwingeGanzzahl"
          class="c-form-control__input"
          data-cy="baujahr"
          min="1800"
          :max="naechstesJahr"
          required
          aria-required="true"
          aria-label="Baujahr des Gebäudes"
          :data-fehlermeldung="baujahrFehlerText"
          placeholder="JJJJ"
        />
        <validierungs-fehler :fehler-text="fehler.baujahr" />
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.isMakler">
      <titel-zelle :zeige-info="true" info-box-id="denkmalschutz"> Steht das Gebäude unter Denkmalschutz?</titel-zelle>
      <zelle class="mkRadio">
        <input
          v-model="piniaStore.state.gebaeudedaten.denkmalschutzManuell"
          type="radio"
          id="nichtDenkmalschutz"
          name="denkmalschutz"
          :value="false"
          data-cy="denkmalNo"
          required
          aria-required="true"
          aria-label="Nein, das Gebäude steht nicht unter Denkmalschutz"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl."
        />
        <label for="nichtDenkmalschutz" class="radiolabel">Nein</label>
        <input
          v-model="piniaStore.state.gebaeudedaten.denkmalschutzManuell"
          type="radio"
          id="denkmalschutz"
          name="denkmalschutz"
          :value="true"
          aria-label="Ja, das Gebäude steht unter Denkmalschutz"
          data-cy="denkmalYes"
        />
        <label for="denkmalschutz" class="radiolabel">Ja</label>
        <validierungs-fehler :fehler-text="fehler.denkmalschutz" :reduzierter-abstand="true" />
        <infotext
          name="denkmalschutz"
          css-klassen="o-grid__cell--no-space"
          text="Sofern das Gebäude unter Denkmalschutz steht, reichen Sie uns bitte den ausgefüllten Denkmalschutzbogen zur Risikoprüfung mit ein. Nach Auswertung des Bogens kann sich der Beitrag ggf. erhöhen."
        >
        </infotext>
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.isMakler && aelterAls30">
      <titel-zelle :zeige-info="true" info-box-id="kernsanierungDurchgefuehrtInfo">
        Wurde eine Kernsanierung durchgeführt?
      </titel-zelle>
      <zelle class="mkRadio">
        <input
          v-model="piniaStore.state.gebaeudedaten.sanierungsdaten.kernsanierungDurchgefuehrt"
          type="radio"
          id="kernsanierungNichtDurchgefuehrt"
          name="kernsanierungDurchgefuehrt"
          :value="false"
          data-cy="kernsanierungNein"
          required
          aria-required="true"
          aria-label="Nein, es wurde keine Kernsanierung durchgeführt"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl."
        />
        <label for="kernsanierungNichtDurchgefuehrt" class="radiolabel">Nein</label>
        <input
          v-model="piniaStore.state.gebaeudedaten.sanierungsdaten.kernsanierungDurchgefuehrt"
          type="radio"
          id="kernsanierungDurchgefuehrt"
          name="kernsanierungDurchgefuehrt"
          :value="true"
          aria-label="Ja, es wurde eine Kernsanierung durchgeführt"
          data-cy="kernsanierungJa"
        />
        <label for="kernsanierungDurchgefuehrt" class="radiolabel">Ja</label>
        <validierungs-fehler :fehler-text="fehler.kernsanierungDurchgefuehrt" :reduzierter-abstand="true" />
        <infotext
          name="kernsanierungDurchgefuehrtInfo"
          css-klassen="o-grid__cell--no-space"
          text="Eine Kernsanierung kann nur dann zu Grunde gelegt werden, wenn die Voraussetzungen gemäß A 27.3, VGB 2018 erfüllt sind. <br />
          Dies sind folgende: <br />
          Neuinstallation der kompletten Wasserzuleitungs- und Ableitungsrohre sowie die
          Heizungsanlage innerhalb und außerhalb des Gebäudes. <br />
          Neuinstallation der Elektroanlage einschließlich ihrer Leitungen. <br />
          Die Erneuerung der Dacheindeckung einschließlich Lattung sowie das mit dem Dach
          verbundene Zubehör."
        >
        </infotext>
      </zelle>
    </zeile>
    <sanierungsfragen
      v-if="piniaStore.state.isMakler && (aelterAls50 || kernsanierungDurchgefuehrt)"
      :aelterAls50="aelterAls50"
      :fehler="fehler"
    />

    <ueberschrift>Angaben zur Bauart</ueberschrift>
    <bauartklasse-ermittlung :fehler="fehler" />
    <ueberschrift>Angaben zur Nutzung</ueberschrift>

    <zeile>
      <titel-zelle><strong>Gebäudenutzung</strong></titel-zelle>
      <zelle>
        <select
          id="gebaeudenutzung"
          name="gebaeudenutzung"
          v-model="piniaStore.state.gebaeudedaten.gebaeudenutzung"
          class="c-form-control-select"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl"
          required
          aria-required="true"
          aria-label="Wie wird das Gebäude genutzt?"
          data-cy="Nutzungsart"
        >
          <option value="0001">Ständig bewohnte Immobilie</option>
          <option value="0002">Ferien-/Wochenendhaus</option>
          <option value="0003">Teilweise leerstehende Immobilie</option>
          <option value="0004">Leerstehende Immobilie</option>
        </select>
        <validierungs-fehler :fehler-text="fehler.gebaeudenutzung" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.gebaeudedaten.gebaeudenutzung === '0003'">
      <titel-zelle><strong>Anteil Leerstand</strong> in %</titel-zelle>
      <zelle>
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="leerstand"
          name="leerstand"
          v-model.number="piniaStore.state.gebaeudedaten.leerstand"
          class="c-form-control__input"
          min="1"
          max="99"
          :required="piniaStore.state.gebaeudedaten.gebaeudenutzung === '0003'"
          :aria-required="piniaStore.state.gebaeudedaten.gebaeudenutzung === '0003'"
          aria-label="Anteil Leerstand in Prozent"
          data-fehlermeldung="Bitte geben Sie den Leerstand zwischen 1 und 99 Prozent an"
          data-cy="LeerstandAnteil"
        />
        <validierungs-fehler :fehler-text="fehler.leerstand" />
      </zelle>
    </zeile>
    <zeile>
      <titel-zelle> Befindet sich ein <strong>Gewerbe</strong> im Gebäude</titel-zelle>
      <zelle class="mkRadio">
        <input
          v-model="piniaStore.state.gebaeudedaten.gewerbeVorhanden"
          type="radio"
          id="keinGewerbe"
          name="gewerbe"
          :value="false"
          required
          aria-required="true"
          aria-label="Nein, es befindet sich kein Gewerbe im Gebäude"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl"
          data-cy="gewerbeNo"
        />
        <label for="keinGewerbe" class="radiolabel">Nein</label>
        <input
          v-model="piniaStore.state.gebaeudedaten.gewerbeVorhanden"
          type="radio"
          id="gewerbe"
          name="gewerbe"
          :value="true"
          aria-label="Ja, es befindet sich ein Gewerbe im Gebäude"
          data-cy="gewerbeYes"
        />
        <label for="gewerbe" class="radiolabel"
          >Ja
          {{ piniaStore.state.gebaeudedaten.gewerbeVorhanden === true ? "(maximal 49% der Gebäudefläche)" : "" }}
        </label>
        <validierungs-fehler :fehler-text="fehler.gewerbe" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <div v-if="piniaStore.state.isMakler && piniaStore.state.gebaeudedaten.gewerbeVorhanden === true">
      <zeile>
        <titel-zelle> Anzahl der Gewerbe</titel-zelle>
        <zelle>
          <select
            id="gewerbeAnzahl"
            name="gewerbeAnzahl"
            v-model.number="piniaStore.state.gebaeudedaten.gewerbeAnzahl"
            class="c-form-control-select"
            data-fehlermeldung="Bitte treffen Sie eine Auswahl"
            :required="piniaStore.state.gebaeudedaten.gewerbeVorhanden === true"
            :aria-required="piniaStore.state.gebaeudedaten.gewerbeVorhanden === true"
            aria-label="Anzahl der Gewerbe im Gebäude"
            data-cy="gewerbeAnzahl"
          >
            <option :value="1">1</option>
            <option :value="2">2</option>
            <option :value="3">3</option>
            <option :value="4">4</option>
          </select>
          <validierungs-fehler :fehler-text="fehler.gewerbeAnzahl" :reduzierter-abstand="true" />
        </zelle>
      </zeile>
      <gewerbeinformationenMakler
        v-for="gewerbeId in piniaStore.state.gebaeudedaten.gewerbeAnzahl"
        :key="gewerbeId"
        :gewerbeId="gewerbeId"
        :fehler="fehler"
      />
    </div>
    <div v-if="!piniaStore.state.isMakler && piniaStore.state.gebaeudedaten.gewerbeVorhanden === true">
      <gewerbeinformationen :gewerbeId="1" :fehler="fehler" />
    </div>

    <zeile>
      <zelle :breite="5" :offset="4">
        <button
          class="c-button c-button--full-width u-mar-t-1 u-mar-b-2 mobil-mehrAbstand"
          @click="nextPage"
          data-cy="submitZumVergleich"
          :disabled="submitDeaktiviert"
        >
          Weiter zur Gebäudeausstattung
        </button>
      </zelle>
    </zeile>
    <modal v-if="showables.toggleLeerstand" @closeModal="toggleLeerstand" titel="Leerstand">
      <template v-slot:inhalt>
        <makler-hinweis />
      </template>
    </modal>
    <modal v-if="showables.toggleFerienhaus" @closeModal="toggleFerienhausCheck" titel="Ferienhaus / Ferienwohnung">
      <template v-slot:inhalt>
        Unser Ferienhauskonzept passt noch besser zu ihrem Ferienobjekt.
        <geschuetzter-router-link v-if="!piniaStore.state.isMakler" to="/fh"
          >Hier geht's direkt zum Tarifrechner.
        </geschuetzter-router-link>
        <a v-if="piniaStore.state.isMakler" href="#" @click="maklerWechsel"> Hier geht's direkt zum Tarifrechner. </a>
      </template>
    </modal>
    <modal
      v-if="showables.toggleDenkmalschutz"
      @closeModal="toggleDenkmalschutz"
      titel="Denkmalschutz"
      :hatSchliessenButton="true"
    >
      <template v-slot:inhalt> Bitte überprüfen Sie die Angabe zum Denkmalschutz.</template>
    </modal>
  </section>
</template>
